<template>
  <AttrGroup
    title="绑定数据"
    :show-add="true"
    :card="false"
    :visible="!!sourceItems.length"
    @add="() => addOrDelSouce('add')"
  >
    <div class="grid gap-2.5">
      <div
        v-for="(item, index) in sourceItems"
        :key="item.stringIndex + '-' + index"
        class="bg-black-card rounded-2xl"
        :class="{
          'pb-2.5': !getFormatOptions(item.sourceKey ?? '', shape.type).length
        }"
      >
        <!-- 当前已选数据源 -->
        <div class="h-13 px-5 flex items-center justify-between w-full">
          <div
            class="cursor-pointer flex items-center"
            @click="() => openSourceModal(item.stringIndex)"
          >
            <span
              class="font-medium text-base underline"
              :class="{
                'attr-disabled': attrDisabled,
                ' !text-sm  !text-white/30': !item.sourceItem
              }"
            >
              {{ item.sourceItem ? item.sourceItem.label : '请选择数据源' }}
            </span>
            <span
              v-if="
                showDefaultValueSources.indexOf(item.sourceKey ?? '') > -1 &&
                canUse('set-default-data-source').valid
              "
              class="ml-1 mb-1"
            >
              <a-tooltip :title="'可到配置区域，进行年份默认值的选择'">
                <icon icon="help.svg" :width="14" :height="14" fill="none" />
              </a-tooltip>
            </span>
          </div>
          <AttrDeleteIcon
            class="opacity-70"
            :class="{
              'attr-disabled': attrDisabled
            }"
            @delete="() => addOrDelSouce('del', item.stringIndex)"
          />
        </div>

        <!-- 数据源返回值 -->
        <DataSourceReturn :source-item="item.sourceItem" />
        <DataSourceFormatUnits
          :shape="shape"
          :string-index="item.stringIndex"
          :disabled="!!attrDisabled"
        />
      </div>
    </div>
  </AttrGroup>

  <modal-data-source
    ref="sourceModal"
    :shape-types="['text']"
    :source-key="shape.attrs?.strings?.[stringIndex]?.value ?? ''"
    :source-tags="['normal', 'subunit_1']"
    @select="onSelectSource"
  />
</template>

<script lang="ts" setup>
import { useDataSource } from '@/hooks';
import { DataSourceFormat } from '@/resources/enum';
import { Shape } from '@/services/shape';
import { Attributes } from '@/services/shape/attributes';
import { useEditorStore } from '@/store/use-editor-store';
import { EventType, emitter } from '@/utils/emitter.utils';
import { PropType, computed, nextTick, ref } from 'vue';
import { Modal } from 'ant-design-vue';
import { GlobalConfig } from '@/services/gloabl.config';
import { canUse } from '@/utils/can-use.utils';

const editorStore = useEditorStore();
const attrDisabled = computed(() => editorStore.attrDisabled);
const { findOne, getFormatOptions } = useDataSource();

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const stringIndex = ref(-1);

const sourceItems = computed(() => {
  const { strings = [] } = props.shape.attrs;
  const res: {
    sourceItem?: DataSourceItem;
    stringIndex: number;
    sourceFormat?: DataSourceFormat;
    sourceKey?: string;
  }[] = [];
  strings.forEach((item, index) => {
    if (item.isSource) {
      res.push({
        sourceItem: findOne(item.value),
        stringIndex: index,
        sourceFormat: item.sourceFormat,
        sourceKey: item.value
      });
    }
  });

  return res;
});

const showDefaultValueSources = [
  'dateYear',
  'dateLunarYear',
  'dateLunarStringYear',
  'dateSexagenaryCycle'
];

const _setAttrs = (data: Partial<Attributes>) => {
  const _data = { ...data };
  props.shape.setAttrs(_data);
};

// 启用或禁用数据源
const addOrDelSouce = (mode: 'add' | 'del', stringIndex = -1) => {
  if (attrDisabled?.value) return;
  if (mode == 'add') {
    if (sourceItems.value.length >= GlobalConfig.maxSourceCountByTextTagBind) {
      return Modal.warning({
        content: `最多只能绑定${GlobalConfig.maxSourceCountByTextTagBind}个数据源`
      });
    }
    openSourceModal(stringIndex);
  } else {
    // delete
    if (stringIndex == undefined) return;
    const { strings = [] } = props.shape.attrs;
    strings.splice(stringIndex, 1);
    // 如果此时为空
    if (!strings.length) {
      strings.push({
        value: '文本',
        color: '#ffffff'
      });
    }
    _setAttrs({ strings });
  }
};

// 数据源modal
const sourceModal = ref();
const openSourceModal = async (index: number) => {
  stringIndex.value = index;
  if (attrDisabled?.value) return;
  await nextTick();
  sourceModal.value?.open?.();
};

// 当选择了数据源
const onSelectSource = (item: DataSourceItem) => {
  const sourceKey = item.key;
  const { strings = [] } = props.shape.attrs;

  // 数据源格式
  const options = getFormatOptions(sourceKey ?? '', props.shape.type);
  const sourceFormat = options?.length ? options?.[0].value : undefined;

  if (
    // 已存在且数据源不一致，直接修改
    strings[stringIndex.value] &&
    strings[stringIndex.value].value != sourceKey
  ) {
    strings[stringIndex.value].value = sourceKey;
    strings[stringIndex.value].sourceFormat = sourceFormat;
  } else {
    // 新增
    if (strings.length == 1 && strings[0].value == '文本') {
      strings.pop();
    }
    strings.push({
      isSource: true,
      color: '#ffffff',
      value: sourceKey,
      sourceFormat
    });
  }
  _setAttrs({ strings });
  emitter.emit(EventType['data-source:selected'], { sourceKey: item.key });
};
</script>
